import { watch, type Directive, type DirectiveBinding, type WatchStopHandle } from 'vue';

import { usePermission } from '@bv/composables/auth/permission';

export interface PermissionElement extends HTMLElement {
  _permissionCleanup?: WatchStopHandle;
  _permissionComment?: Comment;
}

type PermissionBinding = string | string[];

/**
 * Shallow comparison for permission bindings so inline arrays with the same
 * contents do not force a watcher rebuild on every parent re-render.
 */
const isSamePermissionBinding = (next?: PermissionBinding | null, prev?: PermissionBinding | null): boolean => {
  if (next === prev) {
    return true;
  }

  if (typeof next === 'string' || typeof prev === 'string') {
    return next === prev;
  }

  if (!Array.isArray(next) || !Array.isArray(prev)) {
    return false;
  }

  if (next.length !== prev.length) {
    return false;
  }

  return next.every((permission, index) => permission === prev[index]);
};

/**
 * Applies the visibility of the element based on the allowed permission.
 *
 * @param el - The element to apply the visibility to.
 * @param allowed - Whether the element is allowed.
 */
const applyVisibility = (el: PermissionElement, allowed: boolean): void => {
  const comment = el._permissionComment;
  if (!comment) {
    return;
  }

  if (allowed) {
    if (!el.parentNode && comment.parentNode) {
      comment.parentNode.replaceChild(el, comment);
    }
    return;
  }

  if (el.parentNode) {
    el.parentNode.replaceChild(comment, el);
  }
};

/**
 * Binds the permission to the element.
 *
 * @param el - The element to bind the permission to.
 * @param binding - The binding of the permission.
 */
const bindPermission = (el: PermissionElement, binding: DirectiveBinding<PermissionBinding>): void => {
  el._permissionCleanup?.();

  if (!el._permissionComment) {
    el._permissionComment = document.createComment('v-permission');
  }

  if (binding.value == null && import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- only warn in dev
    console.warn('[v-permission] Missing binding; allowing element.');
  }

  const { can } = usePermission();
  const allowed = can(binding.value ?? []);

  applyVisibility(el, allowed.value);

  el._permissionCleanup = watch(allowed, (value) => {
    applyVisibility(el, value);
  });
};

/**
 * Conditionally keeps or removes an element based on user permissions.
 * An empty or missing required list allows the element (deliberate fail-open).
 *
 * @example
 * ```vue
 * <button v-permission="'edit-users'">Edit</button>
 * <button v-permission="['read-users', 'edit-users']">Edit</button>
 * ```
 */
export const permission: Directive<PermissionElement, PermissionBinding> = {
  mounted(el, binding) {
    bindPermission(el, binding);
  },

  updated(el, binding) {
    if (isSamePermissionBinding(binding.value, binding.oldValue)) {
      return;
    }

    bindPermission(el, binding);
  },

  unmounted(el) {
    el._permissionCleanup?.();
    el._permissionComment?.parentNode?.removeChild(el._permissionComment);
    delete el._permissionCleanup;
    delete el._permissionComment;
  },
};
